import { Static, Type } from '@sinclair/typebox';
import { makeNullable } from '../../utils/typeboxHelpers';

export const assetTableSchema = Type.Object({
    id: Type.String({ format: 'uuid' }),
    name: Type.String({ maxLength: 255 }),
    /** The file name of the file that was uploaded */
    filename: Type.String({ maxLength: 255 }),
    /** The unique file name of the generated thumbnail at S3 */
    filenameThumbnail: makeNullable(Type.String({ maxLength: 255 })),
    date: Type.String(),
    /** Size in bytes */
    size: Type.Number(),
    /** Type of asset. Can be an image, video, 3d model, etc or a derivedImage. The latter is an asset generated from a video */
    type: Type.String({ maxLength: 255 }),
    /** Visible in the report for the customer */
    isInConditionReport: Type.Boolean(),
    /** Not in use */
    pixelDistance: Type.Number(),
    /** Asset published in a survey in an element of the composition */
    elementId: makeNullable(Type.String({ format: 'uuid' })),
    /** Asset published in a survey in an unit of the composition */
    unitId: makeNullable(Type.String({ format: 'uuid' })),
    /** Asset belonging to a defect in a survey. Currenlty not used. */
    defectId: makeNullable(Type.String({ format: 'uuid' })),
    /** Asset published in a survey */
    surveyId: makeNullable(Type.String({ format: 'uuid' })),
    /** Not used */
    imageMeasurementId: makeNullable(Type.String({ format: 'uuid' })),
    created_at: makeNullable(Type.String()),
    updated_at: makeNullable(Type.String()),
    /** Asset published in a survey. Survey belongs to this object */
    objectId: makeNullable(Type.String({ format: 'uuid' })),
    /** Extension of the uploaded file */
    fileExtension: Type.String({ maxLength: 255 }),
    /** The order of the assets when they are published in a decomposition part of a survey */
    order: Type.Number(),
    /** Asset was generated from another asset. For example, a still from a video. */
    derivedFromAssetId: makeNullable(Type.String({ format: 'uuid' })),
    /** The downscaling status of an uploaded video */
    downscaleStatus: makeNullable(Type.String()),
    /** Downscale job id */
    downscaleJobId: makeNullable(Type.String({ maxLength: 255 })),
    /** The start time in the original video from which the generated video was created */
    derivedStartTimestamp: makeNullable(Type.Number()),
    /** The end time in the original video from which the generated video was created */
    derivedEndTimestamp: makeNullable(Type.Number()),
    /** The description that appears under a photo, video, or document */
    description: makeNullable(Type.String({ maxLength: 255 })),
    /** The downscaling status for low quality video */
    downscaleStatusLow: makeNullable(Type.String()),
    /** Downscale job id for low quality video */
    downscaleJobLowId: makeNullable(Type.String({ maxLength: 255 })),
    /** The downscaling status for high video */
    downscaleStatusHigh: makeNullable(Type.String()),
    /** Downscale job id for high quality video */
    downscaleJobHighId: makeNullable(Type.String({ maxLength: 255 })),
    /** Asset published in a survey in an manifestation of the composition */
    manifestationId: makeNullable(Type.String({ format: 'uuid' })),
    isInMSWordReport: makeNullable(Type.Boolean()),
    /** Image is used to add annotations, such as a construction drawing */
    is2dSource: makeNullable(Type.Boolean()),
    failureModeId: makeNullable(Type.String({ format: 'uuid' })),
    /** Image is displayed large on the report home page. This can also be an image with annotations added. */
    isOnReportStartPage: makeNullable(Type.Boolean()),
    /** Clicking image opens the URL provided here. If no URL is specified, the image will be displayed in a popup. */
    externalUrl: makeNullable(Type.String({ maxLength: 255 })),
    /** Is not in use */
    inspectionStandardId: makeNullable(Type.String({ format: 'uuid' })),
    /** The unique file name of the generated mid size image at S3. Mid size images are used in the image carousel. */
    filenameMidSize: makeNullable(Type.String({ maxLength: 255 })),
    /** Asset is published with a UT measurements */
    utProjectId: makeNullable(Type.String({ format: 'uuid' })),
    /** The duration of a video */
    duration: makeNullable(Type.Number()),
    /** Documents published to a batch */
    batchId: makeNullable(Type.String({ format: 'uuid' })),
    /** Labels added to documents */
    labels: Type.Any(),
    /** Words in document for full text search */
    document: makeNullable(Type.Unknown()),
});

/** Files such as documents, photos and videos stored in S3 */
export type Asset = Static<typeof assetTableSchema>;
